import { SPECIES, type RegionId } from './species.ts';
import { ELEMENTS, ELEMENT_IDS, type Element } from './elements.ts';
import { AWAKENINGS } from './awakening-data.ts';
import { CONFIG } from './config.ts';
import { killCoins } from './xp.ts';
import { CATCH_CHARM } from './catchcharm.ts';
import { HELD_ITEMS, heldPiece } from './held.ts';
import type { Grade } from './grade.ts';

export type ItemKind = 'seal' | 'potion' | 'revive' | 'ascension' | 'star' | 'token' | 'shard' | 'charm' | 'fragment' | 'crest' | 'tablet' | 'tag' | 'awakening' | 'boost' | 'piece';

/** The Rune Tablet item of an element (shared/tablets.ts): ten Tablet Fragments and a coin fee at the crafting bench. */
export const tabletItem = (el: string) => `rune-tablet-${el}`;
/** What a merchant pays for a Rune Tablet: the ten fragments' worth, never the coin fee, so carving to sell loses coins. */
export const TABLET_SELL = 200_000;

/** Awakening (shared/awakening.ts): shards from boss and Boss Ladder wins, ten make the stone of a chosen species. */
export const AWAKENING_SHARD = 'awakening-shard';
export const AURA_AWAKENING_SHARD = 'aura-awakening-shard';
/** The Awakening Stone of a species (a plain creature), and the Aura Awakening Stone (an Aura creature). */
export const awakeningStone = (species: string) => `awakening-stone-${species}`;
export const auraAwakeningStone = (species: string) => `aura-awakening-stone-${species}`;

/** Timed-boost items (shared/catchcharm.ts useCharmItem): an Aura Charm or a Catch Charm of so many hours, used from the Bag. */
export type CharmItemKind = 'aura' | 'catch';
export const charmItem = (charm: CharmItemKind, hours: 1 | 24) => `charm-${charm}-${hours}h`;

export interface ItemDef {
  id: string;
  name: string;
  kind: ItemKind;
  desc: string;
  /** Coin price in the NPC shop, if sold there. */
  shopPrice?: number;
  /** What an NPC pays when you sell it. */
  sellPrice: number;
  /** Can be listed on the player market (only gameplay drops). */
  tradable: boolean;
  /** Seals: multiplies the catch chance. */
  catchMult?: number;
  heal?: number;
  region?: RegionId;
  /** Ascension stones: the element whose creatures it ascends. */
  element?: Element;
  /** No longer drops anywhere; kept so items already found keep working. */
  legacy?: boolean;
  /** Boost items: using one from the Bag starts or extends this timed boost (shared/catchcharm.ts useCharmItem). */
  boost?: { charm: CharmItemKind; hours: number };
  /** The icon it shows until its own is drawn (client/assets.ts iconUrl). */
  icon?: string;
}

/** The ascension stone of an element: 'flamestone', 'cragstone' and so on. */
export const STONE_NAME: Record<Element, string> = {
  flame: 'Flamestone', tide: 'Tidestone', grove: 'Grovestone', storm: 'Stormstone',
  stone: 'Cragstone', frost: 'Froststone', shade: 'Shadestone', light: 'Lightstone',
};
export const elementStone = (el: Element): string => STONE_NAME[el].toLowerCase();
/** Every ascension stone id, in element order. */
export const STONES: string[] = ELEMENT_IDS.map(elementStone);

/** The trainer title each boss's crest unlocks when crafted (shared/recipes.ts). */
export const CREST_TITLE: Record<'fenrir' | 'typhon' | 'hachimata', string> = { fenrir: 'Wolfbinder', typhon: 'Stormbreaker', hachimata: 'Serpentsbane' };

export const ITEMS: ItemDef[] = [
  { id: 'rune-seal', name: 'Rune Seal', kind: 'seal', desc: 'A plain stone seal cut with one binding rune. Catch chance ×1.', shopPrice: 5, sellPrice: 1, tradable: false, catchMult: 1 },
  { id: 'silver-seal', name: 'Silver Seal', kind: 'seal', desc: 'A seal rimmed in silver; its rune bites deeper. Catch chance ×2.', shopPrice: 20, sellPrice: 5, tradable: false, catchMult: 2 },
  { id: 'gold-seal', name: 'Gold Seal', kind: 'seal', desc: 'A gilded seal that holds a struggling creature longer. Catch chance ×3.', shopPrice: 50, sellPrice: 12, tradable: false, catchMult: 3 },
  { id: 'mythril-seal', name: 'Mythril Seal', kind: 'seal', desc: 'Forged from mythril and all but unbreakable. Catch chance ×4.', shopPrice: 130, sellPrice: 32, tradable: false, catchMult: 4 },
  { id: 'mead', name: 'Healing Mead', kind: 'potion', desc: 'Honeyed mead from a mountain hall. Heals 40% HP.', shopPrice: 40, sellPrice: 10, tradable: false, heal: 0.4 },
  { id: 'nectar', name: 'Nectar Flask', kind: 'potion', desc: 'Nectar of the high meadows, stoppered in a flask. Heals 80% HP.', shopPrice: 120, sellPrice: 30, tradable: false, heal: 0.8 },
  // Used by itself when the whole team faints on a hunt (server/hunt.ts wiped): REVIVE_HP of each fighter's HP, no rest, no XP lost.
  { id: 'revive', name: 'Revive', kind: 'revive', desc: 'Water of the Well of Urd, sealed in a vial. When your whole team faints on a hunt, one brings every fighter back at half HP: no rest and no XP lost.', shopPrice: 500, sellPrice: 125, tradable: false },
  // Ascension stones (shared/items.ts ascensionStone): one per element, a drop from wilds of that element on any hunt.
  // A creature ascends with its line's first element's stone. Free play only: never sold for diamonds or money, and no
  // merchant buys them, so their worth is what players pay on the market.
  ...ELEMENT_IDS.map((el: Element): ItemDef => ({
    id: elementStone(el), name: STONE_NAME[el], kind: 'ascension', sellPrice: 0, tradable: true, element: el,
    desc: `Ascends a ${ELEMENTS[el].name} creature to its next stage. Also trains ${ELEMENTS[el].name} creatures' stats for hunts. Drops from ${ELEMENTS[el].name} wilds.`,
  })),
  // Legacy ascension items (before stones, 2026-10-14). They no longer drop, but ones already found still ascend
  // their region's creatures and still trade.
  { id: 'ygg-sap', name: 'Yggdrasil Sap', kind: 'ascension', desc: 'Golden sap bled from the World Tree. Ascends a Midgard creature to its next stage.', sellPrice: 500, tradable: true, region: 'midgard', legacy: true },
  { id: 'ambrosia', name: 'Ambrosia Drop', kind: 'ascension', desc: 'A drop of the food of the gods. Ascends a Hellas creature to its next stage.', sellPrice: 3000, tradable: true, region: 'hellas', legacy: true },
  { id: 'offering-rice', name: 'Offering Rice', kind: 'ascension', desc: 'Rice left at an underworld shrine. Ascends a Yomi creature to its next stage.', sellPrice: 12_000, tradable: true, region: 'yomi', legacy: true },
  { id: 'rune-of-ascent', name: 'Rune of Ascent', kind: 'star', desc: 'A rune that glows brighter the higher it is carried. Raises a final-stage creature by one star (+10% all stats). Also refines Midgard creatures.', sellPrice: 5000, tradable: true, region: 'midgard' },
  { id: 'olympian-ember', name: 'Olympian Ember', kind: 'star', desc: 'An ember carried down from the mountain of the gods. Raises a final-stage creature by one star (+10% all stats). Needed past 3 stars. Also refines Hellas creatures.', sellPrice: 25_000, tradable: true, region: 'hellas' },
  { id: 'dragon-pearl', name: 'Dragon Pearl', kind: 'star', desc: 'A pearl from a sea dragon\'s hoard. Raises a final-stage creature by one star (+10% all stats). Needed past 5 stars. Also refines Yomi creatures.', sellPrice: 100_000, tradable: true, region: 'yomi' },
  { id: 'fenrir-token', name: 'Chain of Gleipnir', kind: 'token', desc: 'A silken chain like the one that bound the wolf. Lets you challenge Fenrir once.', sellPrice: 10_000, tradable: true, region: 'midgard' },
  { id: 'typhon-token', name: 'Storm Sigil', kind: 'token', desc: 'A sigil that crackles in the hand. Lets you challenge Typhon once.', sellPrice: 50_000, tradable: true, region: 'hellas' },
  { id: 'hachimata-token', name: 'Sacred Rope', kind: 'token', desc: 'A braided rope from a serpent shrine. Lets you challenge Hachimata once.', sellPrice: 200_000, tradable: true, region: 'yomi' },
  { id: 'shrine-shard', name: 'Shrine Shard', kind: 'shard', desc: 'A piece of an old shrine. Ten build one shrine of a random tier. Drops very rarely on Hellas and Yomi hunts.', sellPrice: 2_000, tradable: true, region: 'hellas' },
  // Boss Ladder drops (shared/ladder.ts) and what they make.
  { id: 'tablet-fragment', name: 'Tablet Fragment', kind: 'fragment', desc: 'A chipped piece of a rune tablet, won on the Boss Ladder. Ten make a Rune Tablet of the element you choose at the crafting bench.', sellPrice: 20_000, tradable: true },
  // Rune Tablets, one per element: applied to a creature, its Coverage slot casts that element's coverage strike.
  ...ELEMENT_IDS.map((el: Element): ItemDef => ({
    id: tabletItem(el), name: `${ELEMENTS[el].name} Rune Tablet`, kind: 'tablet', sellPrice: TABLET_SELL, tradable: true,
    desc: `Carved from ten Tablet Fragments. Apply it on a creature's page: its Coverage move becomes ${ELEMENTS[el].name}'s, with the same power and cooldown. Another tablet changes it again.`,
  })),
  // Awakening (shared/awakening.ts): tradeable, never sold to a merchant (sellPrice 0).
  { id: AWAKENING_SHARD, name: 'Awakening Shard', kind: 'awakening', sellPrice: 0, tradable: true, desc: 'A sliver of a gilt rune, won against bosses and on the Boss Ladder. Ten make the Awakening Stone of a species you choose at the crafting bench.' },
  { id: AURA_AWAKENING_SHARD, name: 'Aura Awakening Shard', kind: 'awakening', sellPrice: 0, tradable: true, desc: 'A glowing sliver of a gilt rune, half as common as an Awakening Shard. Ten make the Aura Awakening Stone of a species you choose.' },
  ...AWAKENINGS.flatMap((a): ItemDef[] => [
    { id: awakeningStone(a.from), name: `${SPECIES[a.from].name} Awakening Stone`, kind: 'awakening', sellPrice: 0, tradable: true, region: SPECIES[a.from].region,
      desc: `Awakens a ${SPECIES[a.from].name} (not an Aura) into ${a.name}, on its page. Irreversible.` },
    { id: auraAwakeningStone(a.from), name: `${SPECIES[a.from].name} Aura Awakening Stone`, kind: 'awakening', sellPrice: 0, tradable: true, region: SPECIES[a.from].region,
      desc: `Awakens an Aura ${SPECIES[a.from].name} into ${a.name}; it stays an Aura. Irreversible.` },
  ]),
  { id: 'fenrir-crest-shard', name: 'Fenrir Crest Shard', kind: 'fragment', desc: 'A splinter of a wolf-head crest, won against Fenrir on the Boss Ladder. Ten make the Fenrir Crest at the crafting bench.', sellPrice: 20_000, tradable: true, region: 'midgard' },
  { id: 'typhon-crest-shard', name: 'Typhon Crest Shard', kind: 'fragment', desc: 'A splinter of a storm crest, won against Typhon on the Boss Ladder. Ten make the Typhon Crest at the crafting bench.', sellPrice: 20_000, tradable: true, region: 'hellas' },
  { id: 'hachimata-crest-shard', name: 'Hachimata Crest Shard', kind: 'fragment', desc: 'A splinter of a serpent crest, won against Hachimata on the Boss Ladder. Ten make the Hachimata Crest at the crafting bench.', sellPrice: 20_000, tradable: true, region: 'yomi' },
  // Crests (shared/recipes.ts): made at the bench, kept on the account, and each unlocks its boss's trainer title.
  ...([['fenrir', 'Fenrir', 'A wolf-head crest', 'midgard'], ['typhon', 'Typhon', 'A storm crest', 'hellas'], ['hachimata', 'Hachimata', 'A serpent crest', 'yomi']] as const).map(([b, name, what, region]): ItemDef => ({
    id: `${b}-crest`, name: `${name} Crest`, kind: 'crest', sellPrice: 0, tradable: false, region,
    desc: `${what}. Crafting it unlocks the trainer title ${CREST_TITLE[b]}, yours to wear for good. It stays on your account and changes no stats.`,
  })),
  // Held-item pieces (shared/held.ts): tradeable, never sold to a merchant (sellPrice 0).
  ...HELD_ITEMS.map((d): ItemDef => ({
    id: heldPiece(d.id), name: `${d.name} Piece`, kind: 'piece', sellPrice: 0, tradable: true,
    region: d.boss === 'fenrir' ? 'midgard' : d.boss === 'typhon' ? 'hellas' : 'yomi',
    desc: `A broken-off piece of the ${d.name}, one of ${d.boss === 'fenrir' ? 'Fenrir' : d.boss === 'typhon' ? 'Typhon' : 'Hachimata'}'s held items.`,
  })),
  { id: 'xp-charm', name: 'XP Charm', kind: 'charm', desc: 'Held by one creature, anywhere: it gets 5% of the lead\'s kill XP. Bought with diamonds; stays on your account.', sellPrice: 0, tradable: false },
  { id: 'name-tag', name: 'Name Tag', kind: 'tag', desc: 'Gives one of your creatures a nickname, on its page. Used up on naming; taking a name off is free. Bought with diamonds; stays on your account.', sellPrice: 0, tradable: false },
  // Charms won in ranked seasons (shared/ranked.ts), never sold: they trade for coins only, never gems or diamonds,
  // and the house Exchange never carries them (shared/exchange.ts). Ordinary items until used from the Bag, when they start or
  // extend the timed boost on the trainer. An Aura Charm and a Catch Charm never run together (useCharmItem).
  ...([1, 24] as const).flatMap((h): ItemDef[] => [
    { id: charmItem('aura', h), name: `Aura Charm (${h} h)`, kind: 'boost', sellPrice: 0, tradable: true, boost: { charm: 'aura', hours: h }, icon: 'icon-aura',
      desc: `Use it from the Bag for ${h} h of Aura Charm: Aura odds ×${CONFIG.auraCharm.oddsMult} on your hunt catches, added to any Aura Charm running. Won in ranked seasons. Not usable while a Catch Charm runs.` },
    { id: charmItem('catch', h), name: `Catch Charm (${h} h)`, kind: 'boost', sellPrice: 0, tradable: true, boost: { charm: 'catch', hours: h }, icon: 'icon-relic-catch',
      desc: `Use it from the Bag for ${h} h of +${Math.round((CATCH_CHARM.mult - 1) * 100)}% catch chance on every seal you throw, added to any Catch Charm running. Won in ranked seasons. Not usable while an Aura Charm runs.` },
  ]),
];

export const ITEM: Record<string, ItemDef> = Object.fromEntries(ITEMS.map((i) => [i.id, i]));

/** The Revive (Merchant, 500 coins): a wiped team gets up at this share of its HP (server/hunt.ts wiped). */
export const REVIVE = 'revive';
export const REVIVE_HP = 0.5;
/**
 * Potions and Revives cost this many kills' coins at the trainer's level (killCoins), never less than the listed
 * shopPrice. Their heals are a share of max HP, so a flat price would make them nearly free as income grows; scaled,
 * stocking up costs about the same share of a hunt's coins at every level (pokeidle gets there with fixed-HP potions).
 */
export const PRICE_IN_KILLS: Record<string, number> = { mead: 0.3, nectar: 0.9, revive: 15 };
/** Two significant figures from 100 up, so scaled prices read cleanly. */
const roundPrice = (n: number) => { if (n < 100) return Math.round(n); const p = 10 ** (Math.floor(Math.log10(n)) - 1); return Math.round(n / p) * p; };
/** The Merchant's price for one at this trainer level; undefined when the Merchant doesn't sell it. */
export function shopPriceAt(id: string, level: number): number | undefined {
  const it = ITEM[id];
  if (!it?.shopPrice) return undefined;
  const k = PRICE_IN_KILLS[id];
  return k ? Math.max(it.shopPrice, roundPrice(k * killCoins(level))) : it.shopPrice;
}

/** Legacy ascension items by region (no longer drop; see ascendItems). */
export const ASCENSION_ITEM: Record<RegionId, string> = { midgard: 'ygg-sap', hellas: 'ambrosia', yomi: 'offering-rice' };

/** The stone a species ascends with: its line's first element's (Geodude-style: a Stone/Grove troll takes a Cragstone). */
export function ascensionStone(speciesId: string): string {
  return elementStone(SPECIES[speciesId].elements[0]);
}

/**
 * Items that can ascend this creature, best first: its own stone, then the legacy item of its region. A starter
 * takes its own stone like any creature (Cole 2026-10-28); new trainers find it fast (CONFIG.stones.starterOneIn).
 */
export function ascendItems(c: { species: string; ground: string }): string[] {
  const out = [ascensionStone(c.species)];
  out.push(ASCENSION_ITEM[SPECIES[c.species].region]);
  // Starters grew with Yggdrasil Sap whatever their region before stones (2026-10-14); sap already found still works.
  if (c.ground === 'starter' && !out.includes(ASCENSION_ITEM.midgard)) out.push(ASCENSION_ITEM.midgard);
  return out;
}

/** Chance per kill of each stone from a wild of this species (CONFIG.stones), largest first; the first-stone boost aside. */
export function stoneDrops(speciesId: string): { item: string; p: number }[] {
  const { oneIn, leak } = CONFIG.stones, els = SPECIES[speciesId].elements;
  return ELEMENT_IDS.map((el) => ({ item: elementStone(el), p: ((els.includes(el) ? (1 - leak) / els.length : 0) + leak / ELEMENT_IDS.length) / oneIn }))
    .sort((a, b) => b.p - a.p);
}

/** What this creature would ascend with from this bag: its own stone first, else the first payable item in ascendItems. */
export function ascendItemFrom(c: { species: string; ground: string }, bag: Record<string, number>, pick?: string): string | null {
  const ok = ascendItems(c);
  if (pick && ok.includes(pick) && (bag[pick] ?? 0) > 0) return pick;
  return ok.find((x) => (bag[x] ?? 0) > 0) ?? null;
}
export const STAR_ITEM: Record<RegionId, string> = { midgard: 'rune-of-ascent', hellas: 'olympian-ember', yomi: 'dragon-pearl' };
/** "950 Runes of Ascent", "1 Dragon Pearl": a count with the item's name in the right number. */
export function itemCount(id: string, n: number): string {
  const name = ITEM[id]?.name ?? id;
  // "Aura Charms (1 h)": a name ending in brackets takes the plural before them.
  const many = n === 1 ? name : name.includes(' of ') ? name.replace(/^(\S+)/, '$1s') : / \(.*\)$/.test(name) ? name.replace(/ \(/, 's (') : `${name}s`;
  return `${n.toLocaleString('en-US')} ${many}`;
}

/** Seals in order of strength. */
export const SEALS = ITEMS.filter((i) => i.kind === 'seal').map((i) => i.id);

export const BOSS_TOKEN: Record<string, string> = { fenrir: 'fenrir-token', typhon: 'typhon-token', hachimata: 'hachimata-token' };

/** Most stars a final-stage creature can earn: each region's star material carries it further (CONFIG.maxStarsByRegion). */
export const MAX_STARS = 7;

/** Level a creature needs for the star it is about to earn (1-based): 40, 60, 80 and so on. */
export function starLevelFor(nextStar: number): number {
  return 40 + (nextStar - 1) * 20;
}

/** Item needed for the star a creature is about to earn (1-based). */
export function starItemFor(nextStar: number): string {
  return nextStar <= 3 ? STAR_ITEM.midgard : nextStar <= 5 ? STAR_ITEM.hellas : STAR_ITEM.yomi;
}

// ---- Relics: rolled equipment, one per creature ----
export type RelicStat = 'element' | 'damage' | 'drop' | 'catch' | 'xp' | 'coin';
/**
 * dev: made by an admin command (fast-forward); never tradable. sides: the small extras a relic rolled
 * (RELIC_SIDE); relics made before 2026-10-27 have none and show no grade.
 */
export interface Relic { id: string; stat: RelicStat; element?: string; quality: number; dev?: boolean; sides?: RelicSide[] }

export const RELIC_STATS: { stat: RelicStat; name: string; base: number }[] = [
  { stat: 'element', name: 'Totem', base: 0.2 },
  { stat: 'damage', name: 'Torc', base: 0.1 },
  { stat: 'drop', name: 'Charm', base: 0.12 },
  { stat: 'catch', name: 'Talisman', base: 0.12 },
  { stat: 'xp', name: 'Scroll', base: 0.12 },
  { stat: 'coin', name: 'Purse', base: 0.15 },
];

/** Relic effect as a multiplier: quality 1..100 scales the base bonus from 25% to 100%. */
export function relicMult(r: Relic): number {
  const def = RELIC_STATS.find((d) => d.stat === r.stat)!;
  return 1 + def.base * (0.25 + 0.75 * (r.quality / 100));
}

/**
 * A team's relic perk on the hunt (drop, catch, XP or coins): only its best relic of that kind counts, so a second
 * one adds nothing (2026-10-20; before, every one multiplied: five Q100 Purses paid ×2.01 coins). Sides work the
 * same way: the team's best side of the kind multiplies on top.
 */
export function relicPerk(relics: Iterable<Relic | null | undefined>, kind: RelicStat): number {
  let m = 1, side = 1;
  for (const r of relics) {
    if (!r) continue;
    if (r.stat === kind) m = Math.max(m, relicMult(r));
    for (const x of r.sides ?? []) if (x.stat === kind) side = Math.max(side, relicSideMult(x));
  }
  return m * side;
}

/**
 * Side modifiers (Jonny's idea, Cole 2026-10-27): every new relic rolls one small extra, and two on a rare roll
 * (1 in 25 from the wild, 1 in 10 from a boss). A side is a hunt perk for the whole team, +1% to +4% XP or coins,
 * rolled on the same quality curve as the relic. Never odds (drop or catch chance), so whether a relic can be
 * listed for gems still follows its main stat (shared/gems.ts).
 */
export type RelicSideStat = 'xp' | 'coin';
export interface RelicSide { stat: RelicSideStat; quality: number }
export const RELIC_SIDE: { readonly stats: readonly RelicSideStat[]; readonly base: number; readonly twoOneIn: Readonly<Record<RelicSource, number>> } =
  { stats: ['xp', 'coin'], base: 0.04, twoOneIn: { wild: 25, boss: 10 } };
const SIDE_WORD: Record<RelicSideStat, string> = { xp: 'XP', coin: 'coins' };

export function relicSideMult(x: RelicSide): number {
  return 1 + RELIC_SIDE.base * (0.25 + 0.75 * (x.quality / 100));
}

/** A new relic's sides. rnd: a 0-1 roll. */
export function rollRelicSides(src: RelicSource, rnd: () => number): RelicSide[] {
  const stats = RELIC_SIDE.stats;
  const first = stats[Math.min(stats.length - 1, Math.floor(rnd() * stats.length))];
  const out: RelicSide[] = [{ stat: first, quality: rollRelicQuality(src, rnd()) }];
  if (rnd() < 1 / RELIC_SIDE.twoOneIn[src]) {
    const rest = stats.filter((x) => x !== first);
    out.push({ stat: rest[Math.min(rest.length - 1, Math.floor(rnd() * rest.length))], quality: rollRelicQuality(src, rnd()) });
  }
  return out;
}

/** "+3% XP, +2% coins", or '' for a relic with no sides. */
export function relicSidesText(r: Relic): string {
  return (r.sides ?? []).map((x) => `+${Math.round((relicSideMult(x) - 1) * 100)}% ${SIDE_WORD[x.stat]}`).join(', ');
}

/** The main effect and the sides on one line: "+12% coins · side +3% XP" or "+8% damage · sides +3% XP, +2% coins". */
export function relicLine(r: Relic): string {
  const sides = relicSidesText(r);
  return sides ? `${relicDesc(r)} · ${r.sides!.length > 1 ? 'sides' : 'side'} ${sides}` : relicDesc(r);
}

/**
 * A relic's grade (C to S+), from its quality and its sides: (2 × quality + side + second side) / 4. The cutoffs
 * put wild relics at about half C, 40% B, 9% A, 1 in 100 S and 1 in 1,000 S+ (2,000,000 rolls, 2026-10-27);
 * boss relics roll better. A relic with no sides (made before sides) has no grade.
 */
export const RELIC_GRADE_CUTOFFS: readonly (readonly [Grade, number])[] = [['S+', 72], ['S', 62], ['A', 44], ['B', 16], ['C', 0]];
export function relicScore(r: Relic): number | null {
  if (!r.sides?.length) return null;
  return (2 * r.quality + (r.sides[0]?.quality ?? 0) + (r.sides[1]?.quality ?? 0)) / 4;
}
export function relicGrade(r: Relic): Grade | null {
  const s = relicScore(r);
  if (s === null) return null;
  for (const [g, min] of RELIC_GRADE_CUTOFFS) if (s >= min) return g;
  return 'C';
}

/**
 * Relic quality rolls: Q = min + ⌈(100 − min) × u^exp⌉ (at least 1), so top rolls are rare (2026-10-20; before,
 * 1-100 and 40-100 evenly). Wild hunts: Q95+ about 1 in 50, Q100 about 1 in 300. Boss wins: never under 40, Q95+
 * about 1 in 20 (was 1 in 10), Q100 about 1 in 120, average 60 against the wild's 25. Boss wins make about 3% of
 * relics (npm run economy), so the boss curve hardly moves the top-end supply (an even 40-100 roll: 6% more Q95+);
 * this one keeps a boss relic clearly better than a hunt's.
 */
export type RelicSource = 'wild' | 'boss';
export const RELIC_QUALITY: Readonly<Record<RelicSource, { min: number; exp: number }>> = { wild: { min: 0, exp: 3 }, boss: { min: 40, exp: 2 } };
export function rollRelicQuality(src: RelicSource, u: number): number {
  const { min, exp } = RELIC_QUALITY[src];
  return Math.max(1, Math.min(100, min + Math.ceil((100 - min) * Math.pow(u, exp))));
}
/** Chance a roll from `src` is quality q or lower (the economy projection's buckets). */
export function relicQualityAtMost(src: RelicSource, q: number): number {
  const { min, exp } = RELIC_QUALITY[src];
  if (q >= 100) return 1;
  if (q < Math.max(1, min + 1)) return 0;
  return Math.pow((q - min) / (100 - min), 1 / exp);
}

export function relicName(r: Relic): string {
  const def = RELIC_STATS.find((d) => d.stat === r.stat)!;
  const pre = r.element ? r.element[0].toUpperCase() + r.element.slice(1) + ' ' : '';
  return `${pre}${def.name}`;
}

/** Coins a relic scraps for. */
export const relicScrapCoins = (r: Pick<Relic, 'quality'>) => 50 + r.quality * 5;

/**
 * The bulk scrap (Bag > Relics > Scrap…): relics of the chosen kinds at quality `maxQ` or lower and graded at most
 * `upTo` ('all' = any; a relic made before grades counts as C), beyond the best `keep` of each kind (by grade score,
 * then quality). Only relics in the bag: worn ones never.
 */
export interface BulkScrap { stats: ReadonlySet<RelicStat> | 'all'; maxQ: number; upTo: Grade | 'all'; keep: number }
const GRADE_RANK: Record<Grade, number> = { C: 0, B: 1, A: 2, S: 3, 'S+': 4 };
export function bulkScrapList(relics: readonly Relic[], o: BulkScrap): Relic[] {
  const max = o.upTo === 'all' ? Infinity : GRADE_RANK[o.upTo];
  const byStat = new Map<RelicStat, Relic[]>();
  for (const r of relics) {
    if (o.stats !== 'all' && !o.stats.has(r.stat)) continue;
    const l = byStat.get(r.stat) ?? [];
    l.push(r);
    byStat.set(r.stat, l);
  }
  const out: Relic[] = [];
  for (const list of byStat.values()) {
    list.sort((a, b) => (relicScore(b) ?? b.quality / 2) - (relicScore(a) ?? a.quality / 2) || b.quality - a.quality);
    list.forEach((r, i) => {
      if (i < o.keep || r.quality > o.maxQ) return;
      if (GRADE_RANK[relicGrade(r) ?? 'C'] <= max) out.push(r);
    });
  }
  return out;
}

/** A relic's main bonus in percent, to one decimal so close rolls read apart (Q97 +11.7%, Q100 +12%). */
export function relicPct(r: Relic): number {
  return Math.round((relicMult(r) - 1) * 1000) / 10;
}

export function relicDesc(r: Relic): string {
  const pct = relicPct(r);
  switch (r.stat) {
    case 'element': return `+${pct}% ${r.element} damage`;
    case 'damage': return `+${pct}% damage`;
    case 'drop': return `+${pct}% drop chance`;
    case 'catch': return `+${pct}% catch chance`;
    case 'xp': return `+${pct}% trainer and creature XP`;
    case 'coin': return `+${pct}% coins`;
  }
}
